/**
 * Kind-themed monogram placeholder (colored box + centered icon) — the
 * fallback when a component has no uploaded photo (MenusAsset primary or
 * the legacy `Component.imageUri`). Used by the recipe detail panel.
 *
 * Survivor of the Plan 08 RecipeCard (the universal /recipes grid tile),
 * which was deleted with the catalog browser in the Aug 2026
 * chef-sidebar split.
 */

import { cn } from '@ordo/ui/cn'
import { Beef, Carrot, ChefHat, Cookie, CupSoda, Droplet, Sprout, Wheat } from 'lucide-react'

import { type RecipeComponentKind } from '@/api/recipes'

const KIND_PLACEHOLDER_ICON: Record<RecipeComponentKind, React.ComponentType<{ className?: string }>> = {
  MAIN_ENTREE: Beef,
  SIDE: ChefHat,
  VEGETABLE: Sprout,
  FRUIT: Carrot,
  GRAIN: Wheat,
  BEVERAGE: CupSoda,
  CONDIMENT: Droplet,
  SUPPLEMENT: Cookie,
}

/**
 * Tailwind background classes per kind. We avoid arbitrary hex
 * values so the placeholders track the rest of the design tokens —
 * if global theming flips, the photo placeholders flip too.
 */
const KIND_PLACEHOLDER_BG: Record<RecipeComponentKind, string> = {
  MAIN_ENTREE: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  SIDE: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  VEGETABLE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  FRUIT: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  GRAIN: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  BEVERAGE: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  CONDIMENT: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  SUPPLEMENT: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
}

export function RecipeKindMonogram({
  kind,
  className,
  iconClassName,
}: {
  kind: RecipeComponentKind
  className?: string
  iconClassName?: string
}) {
  const Icon = KIND_PLACEHOLDER_ICON[kind]
  return (
    <div className={cn('grid place-items-center', KIND_PLACEHOLDER_BG[kind], className)}>
      <Icon className={cn('opacity-70', iconClassName ?? 'h-12 w-12')} />
    </div>
  )
}
